import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import { useCompanyGroups } from "~/api/swr/useCompanyGroups";
import { useDocumentShares } from "~/api/v2/useSharedDocuments";
import { shareDocument } from "~/api/v2/requests";
import { useUserContext } from "~/hooks/useUserContext";
import { PlatformRole } from "~/config/types";
import { getDescendantGroups } from "~/helpers/companyGroups";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import { MetadataSection } from "~/molecules/DocumentMetadataSections";

export const DocumentSharingSection = ({
  documentId,
}: {
  documentId: number;
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const { data: groups } = useCompanyGroups();
  const { data: shares, mutate: mutateShares } = useDocumentShares(documentId);

  const descendants = useMemo(() => {
    const activeGroup = groups?.find((group) => group.isActive);
    return groups && activeGroup
      ? getDescendantGroups(groups, activeGroup.id)
      : [];
  }, [groups]);

  if (descendants.length === 0) return null;

  const canShare = user.role !== PlatformRole.reader;

  return (
    <MetadataSection
      title={t(($) => $.sharedDocuments.sharedWith)}
      action={
        canShare && (
          <ShareDialog
            documentId={documentId}
            descendants={descendants}
            sharedGroupIds={shares?.map((share) => share.companyGroupId) ?? []}
            onShared={() => void mutateShares()}
          />
        )
      }
    >
      {shares === undefined || shares.length === 0 ? (
        <span className="text-muted-foreground text-sm">-</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {shares.map((share) => (
            <li
              key={share.companyGroupId}
              className="text-delegation text-sm font-medium"
            >
              {share.name}
            </li>
          ))}
        </ul>
      )}
    </MetadataSection>
  );
};

const ShareDialog = ({
  documentId,
  descendants,
  sharedGroupIds,
  onShared,
}: {
  documentId: number;
  descendants: { id: number; name: string }[];
  sharedGroupIds: number[];
  onShared: () => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const shared = new Set(sharedGroupIds.map(String));
  const options = descendants.map((group) => ({
    value: String(group.id),
    label: group.name,
    disabled: shared.has(String(group.id)),
  }));

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      // All descendants pre-checked; already-shared ones stay checked and
      // disabled so they cannot be unshared.
      setSelectedIds(descendants.map((group) => String(group.id)));
    }
    setOpen(nextOpen);
  };

  const newGroupIds = selectedIds
    .map(Number)
    .filter((id) => !sharedGroupIds.includes(id));

  const submit = async () => {
    try {
      setIsSubmitting(true);
      await shareDocument(documentId, newGroupIds);
      onShared();
      toast.success(t(($) => $.sharedDocuments.documentShared));
      setOpen(false);
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {t(($) => $.sharedDocuments.shareButton)}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-100">
        <DialogHeader>
          <DialogTitle>{t(($) => $.sharedDocuments.shareButton)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.sharedDocuments.shareDescription)}
          </DialogDescription>
        </DialogHeader>
        <ControlledCombobox
          isMulti
          popoverModal
          options={options}
          value={selectedIds}
          onChange={setSelectedIds}
        />
        <DialogFooter>
          <Button
            onClick={() => void submit()}
            disabled={isSubmitting || newGroupIds.length === 0}
          >
            {t(($) => $.sharedDocuments.share)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
